import React, { useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Home, Store, Puzzle } from 'lucide-react';
import { AppShell } from '@next-qr/lib-web-ui/layout';
import type { SidebarItem } from '@next-qr/lib-web-ui/layout';
import { HomePage } from './pages/HomePage';
import { StorePage } from './pages/StorePage';
import { StoreSettingsPage } from './pages/StoreSettingsPage';
import { MiniAppPage } from './pages/MiniAppPage';
import { getInstalledApps } from './miniApps/storage';
import { useTranslation } from 'react-i18next';
import { useConnectPresence } from '@next-qr/lib-ui/presence';
import { apiClient as userClient } from '@next-qr/openapi-client-user';

const iconMap: Record<string, React.ReactNode> = {
  smile: <span>😊</span>,
  camera: <span>📷</span>,
  qrcode: <span>📱</span>,
  video: <span>📹</span>,
  puzzle: <Puzzle size={18} />,
};

export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const installedApps = getInstalledApps();

  // Connect presence WS once for the lifetime of the app shell.
  // This ensures users show as online regardless of which mini-app is active.
  useConnectPresence(userClient);

  const sidebarItems: SidebarItem[] = useMemo(() => [
    {
      id: 'home',
      icon: <Home size={18} />,
      label: t('nav.home'),
      active: location.pathname === '/',
      onClick: () => navigate('/'),
    },
    {
      id: 'store',
      icon: <Store size={18} />,
      label: t('nav.store'),
      active: location.pathname === '/store',
      onClick: () => navigate('/store'),
    },
    ...installedApps.map((app) => ({
      id: app.app_id,
      icon: iconMap[app.icon ?? ''] ?? <Puzzle size={18} />,
      label: app.name,
      active: location.pathname.startsWith(`/app/${app.app_id}`),
      onClick: () => navigate(`/app/${app.app_id}`),
    })),
  ], [location.pathname, installedApps, t, navigate]);

  return (
    <AppShell sidebarItems={sidebarItems}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/store" element={<StorePage />} />
        <Route path="/store/settings" element={<StoreSettingsPage />} />
        <Route path="/app/:appId/*" element={<MiniAppPage />} />
      </Routes>
    </AppShell>
  );
}
